import React from 'react';
import { COOK_METHODS, STYLE_CASING } from '../data/ingredients';
import { solveMeatWeightForCooked } from '../utils/calculations';
import styles from './RecipeSetup.module.css';

const styleOptions = STYLE_CASING.map(s => s[0]);
const styleRec     = Object.fromEntries(STYLE_CASING);

// Preview the basis a target implies, before it is locked in
function solveHint(target, unit, totalPct, calc) {
  const g = unit === 'Imperial (lbs)' ? (parseFloat(target) || 0) * 453.592
                                      : (parseFloat(target) || 0);
  return solveMeatWeightForCooked(g, totalPct, calc.equipLoss, calc.cookLossPct);
}

export default function RecipeSetup({ calc }) {
  const {
    recipeName,   setRecipeName,
    cookMethod,   setCookMethod,
    unitSystem,   setUnitSystem,
    meatWeight,
    sausageStyle, setSausageStyle,
    rating,       setRating,
    recipeNotes,  setRecipeNotes,
    targetCooked, setTargetCooked,
    targetUnit,   setTargetUnit,
    targetLocked, setTargetLocked,
    totalPct,
    setMeatWeightManual,
  } = calc;

  // Locking is only meaningful once there is a formula to solve against
  const canLock = totalPct > 0 && parseFloat(targetCooked) > 0;
  const toggleLock = () => {
    if (targetLocked) { setTargetLocked(false); return; }
    if (canLock) setTargetLocked(true);
  };

  const rec = styleRec[sausageStyle];

  return (
    <div className={styles.bar}>
      <div className={styles.barRow}>

        {/* 1. Recipe Name */}
        <div className={`${styles.field} ${styles.fieldWide}`}>
          <label className={styles.label}>Recipe Name</label>
          <input
            className={styles.input}
            type="text"
            placeholder="e.g. Smoked Kielbasa v2"
            value={recipeName}
            onChange={e => setRecipeName(e.target.value)}
          />
        </div>

        {/* 2. Sausage Style */}
        <div className={`${styles.field} ${styles.fieldMed}`}>
          <label className={styles.label}>Sausage Style</label>
          <select
            className={styles.select}
            value={sausageStyle}
            onChange={e => setSausageStyle(e.target.value)}
          >
            {styleOptions.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          {rec && (
            <div className={styles.casingRec} title={rec}>{rec}</div>
          )}
        </div>

        {/* 3. Meat Weight — input + unit dropdown together */}
        <div className={styles.field}>
          <label className={styles.label}>Meat+Fat Weight Basis</label>
          <div className={styles.weightGroup}>
            <input
              className={`${styles.input} ${styles.inputNum} ${targetLocked ? styles.inputSolved : ''}`}
              type="text"
              inputMode="decimal"
              value={meatWeight}
              onChange={e => setMeatWeightManual(e.target.value)}
              title={targetLocked
                ? 'Solved from the target finished weight — typing here releases the lock'
                : undefined}
            />
            <select
              className={styles.unitSelect}
              value={unitSystem}
              onChange={e => setUnitSystem(e.target.value)}
            >
              <option value="Metric (g)">g</option>
              <option value="Imperial (lbs)">lbs</option>
            </select>
          </div>
          <div className={styles.weightConv}>
            {unitSystem === 'Metric (g)'
              ? `${(meatWeight / 453.592).toFixed(3)} lbs`
              : `${(meatWeight * 453.592).toFixed(0)} g`}
            {targetLocked && <span className={styles.solvedTag}>solved from target</span>}
          </div>
        </div>

        {/* 3b. Target finished weight — solves the basis backwards */}
        <div className={styles.field}>
          <label className={styles.label}>Target Finished</label>
          <div className={styles.weightGroup}>
            <input
              className={`${styles.input} ${styles.inputNum}`}
              type="text"
              inputMode="decimal"
              placeholder="e.g. 10"
              value={targetCooked}
              onChange={e => setTargetCooked(e.target.value)}
            />
            <select
              className={styles.unitSelect}
              value={targetUnit}
              onChange={e => setTargetUnit(e.target.value)}
            >
              <option value="Imperial (lbs)">lb</option>
              <option value="Metric (g)">g</option>
            </select>
            <button
              type="button"
              className={`${styles.lockBtn} ${targetLocked ? styles.lockBtnOn : ''}`}
              onClick={toggleLock}
              disabled={!targetLocked && !canLock}
              title={targetLocked
                ? 'Locked — the basis re-solves as you edit the recipe. Click to release.'
                : canLock
                  ? 'Lock this target and solve the meat+fat basis for it'
                  : 'Enter a target and add ingredients first'}
            >
              {targetLocked ? '🔒' : '🔓'}
            </button>
          </div>
          <div className={styles.weightConv}>
            {targetLocked
              ? `holds at ${totalPct.toFixed(1)}% formula`
              : canLock
                ? `needs ${(solveHint(targetCooked, targetUnit, totalPct, calc) / 453.592).toFixed(2)} lb meat+fat`
                : 'after cook loss'}
          </div>
        </div>

        {/* 4. Cooking Method */}
        <div className={styles.field}>
          <label className={styles.label}>Cooking Method</label>
          <div className={styles.pillGroup}>
            {COOK_METHODS.map(m => (
              <button
                key={m}
                className={`${styles.pill} ${cookMethod === m ? styles.pillActive : ''}`}
                onClick={() => setCookMethod(m)}
              >
                {m}
              </button>
            ))}
          </div>
          {cookMethod === 'Cold Smoked' && (
            <div className={styles.cureWarning}>⚠️ Cure #1 required</div>
          )}
        </div>

        {/* 5. Rating */}
        <div className={styles.field}>
          <label className={styles.label}>Rating</label>
          <div className={styles.stars}>
            {[1,2,3,4,5].map(n => (
              <button
                key={n}
                className={`${styles.star} ${rating >= n ? styles.starActive : ''}`}
                onClick={() => setRating(rating === n ? '' : n)}
              >★</button>
            ))}
          </div>
        </div>

      </div>

      {/* Notes row */}
      <div className={styles.notesRow}>
        <label className={styles.label}>Recipe Notes</label>
        <textarea
          className={styles.notes}
          placeholder="Modifications, observations, batch notes..."
          value={recipeNotes}
          onChange={e => setRecipeNotes(e.target.value)}
          rows={2}
        />
      </div>
    </div>
  );
}
